import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Catches the failure mode that route-level code-splitting introduces: a lazy
 * chunk that 404s after a deploy.
 *
 * `<Suspense>` handles a *pending* import promise, not a *rejected* one. A
 * rejected `React.lazy` import throws during render, and with React 18
 * `createRoot` an uncaught render error unmounts the entire root — a permanent
 * white screen, not the spinner the Suspense fallback suggests.
 *
 * That is routine here rather than theoretical, because of how this app ships:
 * nginx serves /assets/ as `immutable` with `try_files $uri =404`, and the
 * container is replaced wholesale on deploy, so old hashed chunks disappear the
 * instant a new image goes live. A user holding a cached index.html who clicks
 * a nav link then requests a chunk that no longer exists.
 *
 * A reload fixes it, because it re-fetches index.html and resolves the current
 * hashes. So for chunk-load errors specifically we reload once, automatically.
 *
 * Two deliberate constraints:
 *
 *  - Only chunk-load errors reload. A genuine render bug reloading itself would
 *    hide the bug and spin. Anything else renders a visible error instead.
 *  - The reload is one-shot, guarded by sessionStorage. If the chunk is still
 *    missing after reloading (a genuinely broken deploy, not a stale shell),
 *    we stop and show the error rather than loop.
 */

const RELOAD_GUARD_KEY = 'nu:chunk-reload-attempted'

/**
 * Chunk-load failures have no single standard shape — the wording is per-engine
 * and per-bundler, so this matches on the known set rather than one string.
 * A false negative is safe (we show an error instead of reloading); a false
 * positive is not (an unrelated bug would trigger a reload), so these patterns
 * are deliberately specific to module fetching.
 */
function isChunkLoadError(error: Error): boolean {
  if (error.name === 'ChunkLoadError') return true

  return [
    /Failed to fetch dynamically imported module/i, // Chrome / Edge
    /error loading dynamically imported module/i, // Firefox
    /Importing a module script failed/i, // Safari
    /Loading chunk \d+ failed/i, // webpack
    /Loading CSS chunk/i, // split CSS
  ].some((pattern) => pattern.test(error.message))
}

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      console.error('Unhandled render error', error, info.componentStack)
      return
    }

    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      // Already reloaded once and the chunk is still unreachable. Reloading
      // again would loop, so surface it instead.
      console.error('Chunk still unreachable after reload', error)
      return
    }

    sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
    window.location.reload()
  }

  /**
   * Clears the guard once the app renders successfully, so a later deploy gets
   * its own reload attempt rather than inheriting a spent one from this session.
   */
  componentDidMount(): void {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    // Reached only when the reload did not resolve it, or the error was never
    // a chunk-load failure. Plain markup with no imports of its own — anything
    // this fallback depended on could be the very thing that failed to load.
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '1rem',
          fontFamily: 'system-ui, sans-serif',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', margin: 0 }}>Something went wrong</h1>
        <p style={{ margin: 0, color: '#555', maxWidth: '32rem' }}>
          This page failed to load. Reloading usually fixes it. If it keeps
          happening, your session may be running against an outdated version.
        </p>
        <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem(RELOAD_GUARD_KEY)
            window.location.reload()
          }}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: '1px solid #ccc',
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}
